import React from 'react';
import { 
  Users, 
  Calendar, 
  CreditCard, 
  FileText, 
  BarChart3, 
  Shield, 
  Settings, 
  Globe, 
  Smartphone, 
  Database,
  Bell,
  Package,
  MapPin,
  Clock,
  UserCheck,
  FileCheck,
  MessageSquare,
  Zap,
  Sparkles,
  Rocket,
  Gem,
  TrendingUp
} from 'lucide-react';

const FeaturesPage: React.FC = () => {
  const clientFacingFeatures = [
    {
      icon: Globe,
      title: 'Professional Business Website',
      description: 'Fully customizable website with your branding, colors, and content'
    },
    {
      icon: Calendar,
      title: 'Online Booking System',
      description: 'Real-time appointment scheduling with automated confirmations'
    },
    {
      icon: Users,
      title: 'Client Portal & Dashboard',
      description: 'Secure client area with booking history and account management'
    },
    {
      icon: Package,
      title: 'Service Catalog Display',
      description: 'Beautiful showcase of your services with pricing and descriptions'
    },
    {
      icon: MessageSquare,
      title: 'Contact Forms & Communication',
      description: 'Multiple contact forms and business information display'
    },
    {
      icon: Smartphone,
      title: 'Mobile-Responsive Design',
      description: 'Perfect experience on all devices and screen sizes'
    },
    {
      icon: Zap,
      title: 'SEO Optimization',
      description: 'Built-in SEO features to help your business get found online'
    },
    {
      icon: Globe,
      title: 'Custom Domain Setup',
      description: 'Use your own domain name for complete branding control'
    }
  ];

  const managementSystemFeatures = [
    {
      icon: BarChart3,
      title: 'Executive Dashboard',
      description: 'Real-time business analytics with revenue, bookings, and performance metrics'
    },
    {
      icon: Users,
      title: 'Client Management System',
      description: 'Complete customer database with history, preferences, and communication logs'
    },
    {
      icon: Calendar,
      title: 'Advanced Booking Engine',
      description: 'Multi-location scheduling with staff assignments and resource management'
    },
    {
      icon: CreditCard,
      title: 'Financial Management',
      description: 'Invoicing, payment processing, expense tracking, and financial reporting'
    },
    {
      icon: UserCheck,
      title: 'Staff Management Portal',
      description: 'Employee scheduling, payroll integration, and performance tracking'
    },
    {
      icon: MapPin,
      title: 'Multi-Location Control',
      description: 'Centralized management for multiple business locations and branches'
    },
    {
      icon: FileText,
      title: 'Document Management',
      description: 'Client files, contracts, forms, and digital document storage system'
    },
    {
      icon: Shield,
      title: 'Security & Permissions',
      description: 'Role-based access control with advanced user permissions and audit trails'
    },
    {
      icon: Database,
      title: 'Data Analytics Center',
      description: 'Custom reports, trend analysis, and business intelligence dashboards'
    },
    {
      icon: Settings,
      title: 'System Configuration',
      description: 'White-label customization, API integrations, and platform settings'
    },
    {
      icon: Smartphone,
      title: 'Mobile Management App',
      description: 'Full-featured mobile app for managing your business on the go'
    }
  ];

  const businessModelFlexibility = [
    {
      model: 'SaaS Subscription',
      description: 'Customize and offer monthly/yearly subscriptions under your brand',
      potential: '$50-500/month per client'
    },
    {
      model: 'One-Time License',
      description: 'Rebrand and provide one-time solutions to businesses',
      potential: '$5,000-50,000 per license'
    },
    {
      model: 'Custom Enterprise',
      description: 'Transform into custom enterprise solutions for large organizations',
      potential: '$25,000-250,000 per project'
    },
    {
      model: 'Niche-Specific Platform',
      description: 'Fully customize for specific industries and launch as your own branded SaaS',
      potential: 'Unlimited scalability'
    }
  ];

  const managementFeatures = [
    {
      category: 'Executive Dashboard',
      icon: BarChart3,
      items: [
        'Real-time business metrics and KPIs',
        'Revenue tracking and financial overview',
        'Appointment and booking overview',
        'Staff performance summaries',
        'Client growth analytics'
      ]
    },
    {
      category: 'Client Management',
      icon: Users,
      items: [
        'Complete client database with profiles',
        'Service history and preferences',
        'Communication logs and notes',
        'Client segmentation and tagging',
        'Automated follow-up reminders'
      ]
    },
    {
      category: 'Advanced Booking System',
      icon: Calendar,
      items: [
        'Smart calendar management',
        'Multi-service appointment scheduling',
        'Automated booking confirmations',
        'Waitlist management',
        'Recurring appointment support'
      ]
    },
    {
      category: 'Service Management',
      icon: Package,
      items: [
        'Complete service catalog management',
        'Dynamic pricing controls',
        'Duration and resource settings',
        'Staff assignment rules',
        'Service package creation'
      ]
    },
    {
      category: 'Staff & Provider Management',
      icon: UserCheck,
      items: [
        'Team profiles and permissions',
        'Schedule and availability management',
        'Performance tracking and analytics',
        'Commission and payment tracking',
        'Role-based access controls'
      ]
    },
    {
      category: 'Financial Management',
      icon: CreditCard,
      items: [
        'Comprehensive revenue tracking',
        'Multiple payment gateway support',
        'Automated billing and invoicing',
        'Tax calculation and reporting',
        'Refund and dispute management'
      ]
    },
    {
      category: 'Reporting & Analytics',
      icon: BarChart3,
      items: [
        'Revenue and profit analytics',
        'Client behavior insights',
        'Staff performance reports',
        'Booking trend analysis',
        'Financial summaries and forecasting'
      ]
    },
    {
      category: 'Security & Compliance',
      icon: Shield,
      items: [
        'User roles and permissions',
        'Complete audit logs',
        'Data encryption and security',
        'GDPR compliance tools',
        'Backup and recovery systems'
      ]
    },
    {
      category: 'Document Management',
      icon: FileText,
      items: [
        'Client file storage',
        'Contract and agreement templates',
        'Digital signature support',
        'Document versioning',
        'Secure file sharing'
      ]
    },
    {
      category: 'Inventory Management',
      icon: Package,
      items: [
        'Product and supply tracking',
        'Stock level monitoring',
        'Supplier management',
        'Purchase order automation',
        'Cost tracking and analysis'
      ]
    },
    {
      category: 'Multi-Location Support',
      icon: MapPin,
      items: [
        'Manage multiple business locations',
        'Location-specific scheduling',
        'Centralized reporting across locations',
        'Individual location branding',
        'Staff assignment by location'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-innovation text-white relative overflow-hidden">
      {/* Global Animated Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-transparent"></div>
      <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-2xl animate-pulse" style={{animationDelay: '4s'}}></div>
      <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-blue-500/8 rounded-full blur-3xl animate-pulse" style={{animationDelay: '6s'}}></div>
      
      {/* Hero Section */}
      <section className="py-20 relative z-10 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-cyan-300 bg-clip-text text-transparent">
            Everything You Need to Build<br />Your Business Empire
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mx-auto leading-relaxed">
            Launch your professional platform in 24 hours with our complete white-label system. 
            <span className="text-cyan-300 font-semibold"> From stunning client websites to powerful management system</span> - 
            everything is included to help you <span className="text-yellow-300 font-semibold">dominate your market</span>.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-gradient-to-r from-green-500 to-emerald-400 text-white px-6 py-3 rounded-full text-lg font-bold shadow-lg">
              <Sparkles className="inline h-5 w-5 mr-2" />
              $50K+ Platform Value
            </div>
            <div className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-6 py-3 rounded-full text-lg font-bold shadow-lg">
              <Rocket className="inline h-5 w-5 mr-2" />
              Launch in 24 Hours
            </div>
            <div className="bg-gradient-to-r from-purple-500 to-pink-400 text-white px-6 py-3 rounded-full text-lg font-bold shadow-lg">
              <Gem className="inline h-5 w-5 mr-2" />
              100% Ownership
            </div>
          </div>
        </div>
      </section>

      {/* What You Get Overview */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Your Complete Business-in-a-Box Solution
            </h2>
            <p className="text-xl text-slate-300 max-w-4xl mx-auto">
              Two powerful systems working together: A professional client-facing website that converts visitors into customers, 
              plus a comprehensive management system that manages your entire operation seamlessly.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Client-Facing Features */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 hover:border-cyan-400/30 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <Globe className="h-8 w-8 text-cyan-400 mr-3" />
                Client-Facing Website Features
              </h3>
              <div className="space-y-6">
                {clientFacingFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="bg-cyan-400/10 p-2 rounded-lg flex-shrink-0">
                      <feature.icon className="h-6 w-6 text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                      <p className="text-slate-300 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Management System Preview */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 hover:border-cyan-400/30 transition-all duration-300">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                <Settings className="h-8 w-8 text-cyan-400 mr-3" />
                Management System Dashboard
              </h3>
              <div className="space-y-6">
                {managementSystemFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="bg-cyan-400/10 p-2 rounded-lg flex-shrink-0">
                      <feature.icon className="h-6 w-6 text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                      <p className="text-slate-300 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Management Features */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Management System Dashboard - Detailed Breakdown
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Every feature and capability you need to run a professional business management platform
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {managementFeatures.map((feature, index) => (
              <div key={index} className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 hover:shadow-lg transition-shadow duration-200">
                <div className="flex items-center mb-4">
                  <div className="bg-cyan-400/10 p-3 rounded-lg mr-4">
                    <feature.icon className="h-6 w-6 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{feature.category}</h3>
                </div>
                <ul className="space-y-2">
                  {feature.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start space-x-3">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-slate-200">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Excellence */}
      <section className="py-20 relative z-10 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Built for Scale & Performance
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Enterprise-grade architecture designed to handle growth from day one
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Database className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Scalable Architecture</h3>
              <p className="text-gray-300">
                Handles 1 to 10,000+ users seamlessly with cloud-ready deployment
              </p>
            </div>
            <div className="text-center">
              <div className="bg-primary-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Enterprise Security</h3>
              <p className="text-gray-300">
                Bank-level security with encryption, audit logs, and compliance features
              </p>
            </div>
            <div className="text-center">
              <div className="bg-primary-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Modern Tech Stack</h3>
              <p className="text-gray-300">
                Built with latest technologies for optimal performance and maintainability
              </p>
            </div>
          </div>

          <div className="mt-16 bg-gray-800 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-center mb-8">Technical Specifications</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <h4 className="font-semibold text-primary-400 mb-2">Frontend</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• React.js / Vue.js</li>
                  <li>• Responsive design</li>
                  <li>• Progressive Web App</li>
                  <li>• Mobile optimization</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-primary-400 mb-2">Management System</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• Node.js / PHP</li>
                  <li>• RESTful API</li>
                  <li>• Database optimization</li>
                  <li>• Caching systems</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-primary-400 mb-2">Security</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• SSL/TLS encryption</li>
                  <li>• GDPR compliance</li>
                  <li>• Data backup systems</li>
                  <li>• Access controls</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-primary-400 mb-2">Integrations</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• Payment gateways</li>
                  <li>• Calendar systems</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Model Flexibility */}
      <section className="py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Unlimited Business Model Flexibility
            </h2>
            <p className="text-xl text-slate-300 max-w-4xl mx-auto">
              Get this ready business-in-a-box for just <span className="text-yellow-300 font-bold">$799</span> or <span className="text-yellow-300 font-bold">$1,299</span> and 
              sell it using multiple pricing models simultaneously. <span className="text-cyan-300 font-semibold">Your investment pays for itself with just one client!</span>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {businessModelFlexibility.map((model, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:shadow-lg transition-shadow duration-200">
                <h3 className="text-xl font-bold text-white mb-2">{model.model}</h3>
                <p className="text-slate-300 mb-4">{model.description}</p>
                <div className="bg-cyan-100 p-3 rounded-lg">
                  <span className="text-cyan-900 font-semibold">Revenue Potential: </span>
                  <span className="text-slate-900 font-bold">{model.potential}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-500/20 to-blue-500/20 backdrop-blur-sm border border-green-300/30 rounded-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-400 font-bold">LIVE PRODUCTION SYSTEM</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  ClinicIT - Built with Our Platform
                </h3>
                <p className="text-slate-300">Real medical practice management system serving actual clinics</p>
              </div>
              <a 
                href="https://clinicit.live" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
              >
                Open Full Site
                <Globe className="h-5 w-5" />
              </a>
            </div>

            <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div className="bg-slate-800 rounded px-3 py-1 text-slate-300 text-sm font-mono">
                  https://clinicit.live
                </div>
              </div>
              
              <div className="relative">
                <iframe
                  src="https://clinicit.live"
                  className="w-full h-[500px] rounded-lg border border-slate-600/50"
                  title="ClinicIT Live Demo"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-slate-900/10 pointer-events-none rounded-lg"></div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <div className="text-cyan-400 font-bold text-lg">Unified Booking</div>
                <div className="text-slate-300 text-sm">Online + Walk-in Management</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <div className="text-cyan-400 font-bold text-lg">HIPAA Compliant</div>
                <div className="text-slate-300 text-sm">Medical Industry Ready</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <div className="text-cyan-400 font-bold text-lg">Live Production</div>
                <div className="text-slate-300 text-sm">Serving Real Clinics</div>
              </div>
            </div>

            <div className="mt-6 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-400/20 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Sparkles className="h-6 w-6 text-cyan-400" />
                <h4 className="text-lg font-bold text-white">Created from Our $1,299 Platform</h4>
              </div>
              <p className="text-slate-300 text-sm">
                This medical practice management system was built by customizing our white-label platform for the healthcare industry. 
                Same platform, unlimited customization possibilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 relative z-10 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Own This Complete System?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Get all these features and more with full source code and white-label rights
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/pricing"
              className="bg-yellow-400 text-slate-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-300 transition-colors duration-200"
            >
              See Pricing Options
            </a>
            <a
              href="/demo"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-white/5 backdrop-blur-sm hover:text-cyan-400 transition-colors duration-200"
            >
              Try Live Demo
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;
